import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { isAccountTypeChange } from '@/lib/account-type-change';
import type { AuthState } from '@/lib/auth';
import type { QueryClient } from '@tanstack/react-query';
import { RootLayout } from '@/routes/__root';
import { LoginPage } from '@/routes/login';
import { DecouvertePage } from '@/routes/decouverte';
import { ScratchTutorialPage } from '@/features/scratch-tutorial/scratch-tutorial-page';
import { parseTutorialSearch } from '@/features/scratch-tutorial/first-use';
import {
  hasSeenDecouverte,
  shouldShowDecouverteBeforeLogin,
} from '@/features/decouverte/decouverte-seen';
import { AppLayout } from '@/routes/_app';
import { ExercisePage } from '@/routes/_app.learn.$subject.$level.$chapter.$exercise';
import { DagPlayerPage } from '@/routes/_app.dag.$exerciseId';
import { BlogArticleReaderPage } from '@/routes/_app.blog.$slug';
import { TutorLayout } from '@/routes/_app.tutor';
import { TutorDashboardPage } from '@/routes/_app.tutor.dashboard';
import { TutorSubscriptionPage } from '@/routes/_app.tutor.abonnement';
import { TutorClassesPage } from '@/routes/_app.tutor.classes';
import { TutorClassNewPage } from '@/routes/_app.tutor.classes.new';
import { TutorClassDetailPage } from '@/routes/_app.tutor.classes.$classId';
import { TutorStudentDetailPage } from '@/routes/_app.tutor.students.$studentId';
import { TutorExercisesPage } from '@/routes/_app.tutor.exercises';
import { TutorContactsPage } from '@/routes/_app.tutor.contacts';
import { TutorFilesPage } from '@/routes/_app.tutor.files';
import { TutorDiscussionsPage } from '@/routes/_app.tutor.discussions';
import { TutorBookingDashboardPage } from '@/routes/_app.tutor.rendez-vous';
import { TutorBookingSlotsPage } from '@/routes/_app.tutor.rendez-vous.creneaux';
import { TutorBookingPaymentsPage } from '@/routes/_app.tutor.rendez-vous.paiements';
import { TutorVisioSeriesPage } from '@/routes/_app.tutor.rendez-vous.series';
import { TutorNewRendezVousPage } from '@/routes/_app.tutor.rendez-vous.new';
import { TutorNewMeetingPage } from '@/routes/_app.tutor.rendez-vous.reunion';
import { FreeMeetingRoomPage } from '@/routes/_app.reunion.$roomId.room';
import { TutorExercisesNewPage } from '@/routes/_app.tutor.exercises.new';
import { TutorExercisesNewV2Page } from '@/routes/_app.tutor.exercises.new-v2';
import { TutorExerciseDetailPage } from '@/routes/_app.tutor.exercises.$exerciseId';
import { TutorExerciseEditV2Page } from '@/routes/_app.tutor.exercises.$exerciseId.edit-v2';
import { TutorExerciseTestPage } from '@/routes/_app.tutor.exercises.$exerciseId.test';
import { TutorAdminLayout } from '@/routes/_app.tutor.admin';
import { TutorAdminChaptersPage } from '@/routes/_app.tutor.admin.chapters';
import { TutorAdminChaptersNewPage } from '@/routes/_app.tutor.admin.chapters.new';
import { TutorAdminChapterDetailPage } from '@/routes/_app.tutor.admin.chapters.$chapterId';
import { TutorAdminChapterEditPage } from '@/routes/_app.tutor.admin.chapters.$chapterId.edit';
import { TutorAdminLessonDetailPage } from '@/routes/_app.tutor.admin.lessons.$lessonId';
import { TutorAdminLessonEditPage } from '@/routes/_app.tutor.admin.lessons.$lessonId.edit';
import { TutorAdminLessonEditV2Page } from '@/routes/_app.tutor.admin.lessons.$lessonId.edit-v2';
import { TutorAdminCompetenceEditorPage } from '@/routes/_app.tutor.admin.competences.$competenceId';
import { TutorAdminPromptsPage } from '@/routes/_app.tutor.admin.prompts';
import { TutorAdminObjectivesPage } from '@/routes/_app.tutor.admin.objectives';
import { TutorAdminFaqPage } from '@/routes/_app.tutor.admin.faq';
import { TutorAdminSkillcardPricingPage } from '@/routes/_app.tutor.admin.skillcards-pricing';
import { TutorAdminUsersPage } from '@/routes/_app.tutor.admin.users';
import { TutorAdminAlwaysdataPage } from '@/routes/_app.tutor.admin.alwaysdata';
import { TutorAdminObjectivesNewPage } from '@/routes/_app.tutor.admin.objectives.new';
import { TutorAdminObjectiveDetailPage } from '@/routes/_app.tutor.admin.objectives.$objectiveId';
import { TutorAdminEpreuvesPage } from '@/routes/_app.tutor.admin.epreuves';
import { TutorAdminEpreuvesNewPage } from '@/routes/_app.tutor.admin.epreuves.new';
import { TutorAdminEpreuveDetailPage } from '@/routes/_app.tutor.admin.epreuves.$epreuveId';
import { TutorBlogPage } from '@/routes/_app.tutor.blog';
import { TutorBlogNewPage } from '@/routes/_app.tutor.blog.new';
import { TutorBlogDetailPage } from '@/routes/_app.tutor.blog.$articleId';
import { TutorBlogStatsPage } from '@/routes/_app.tutor.blog.$articleId.stats';
import { TutorAdminBlogModerationPage } from '@/routes/_app.tutor.admin.blog.moderation';
import { TutorAdminReportsPage } from '@/routes/_app.tutor.admin.reports';
import { TutorAdminStatsPage } from '@/routes/_app.tutor.admin.stats';
import { TutorAdminScratchFeedbackPage } from '@/routes/_app.tutor.admin.scratch-feedback';
import { TutorAdminAndroidTestersPage } from '@/routes/_app.tutor.admin.android-testers';
import { TutorAdminSocialConnectedPage } from '@/routes/_app.tutor.admin.social.connected';
import { TutorAdminSocialSettingsPage } from '@/routes/_app.tutor.admin.social.settings';
import { TutorAdminConnectionsPage } from '@/routes/_app.tutor.admin.connexions';
import { TutorAdminSchoolsPage } from '@/routes/_app.tutor.admin.etablissements';
import { TutorAdminNewsletterPage } from '@/routes/_app.tutor.admin.newsletter';
import { TutorAdminDagPage } from '@/routes/_app.tutor.admin.dag';
import { TutorBlueprintsPage } from '@/routes/_app.tutor.blueprints';
import { TutorBlueprintsNewPage } from '@/routes/_app.tutor.blueprints.new';
import { TutorBlueprintDetailPage } from '@/routes/_app.tutor.blueprints.$blueprintId';
import { TutorCardsPage } from '@/routes/_app.tutor.cards';
import { consumeReturnTo } from '@/lib/return-to';
import { PublicCardPage } from '@/routes/card.$cardId';
import { PublicAnimationPage } from '@/routes/animation.$slug';
import { CaptureAnimationPage } from '@/routes/capture.animation';
import { SkillcardBuyPage } from '@/routes/card.$cardId.acheter';
import { PackBuyPage } from '@/routes/pack.$packId.acheter';
import { ExpressBuyPage } from './routes/debloquer.$offer.$packId';
import { PayForStudentPage } from '@/routes/payer.$token';
import { CardShortCodePage } from '@/routes/c.$code';
import { TutorClassProgressionPage } from '@/routes/_app.tutor.classes.$classId.progression';
import { TutorAdminPacksPage } from '@/routes/_app.tutor.admin.packs';
import { TutorAdminToolboxPage } from '@/routes/_app.tutor.admin.toolbox';
import { TutorAdminTroussesPage } from '@/routes/_app.tutor.admin.trousses';
import { TutorAdminAnimationsPage } from '@/routes/_app.tutor.admin.animations';
import { TutorAdminAnimationLabPage } from '@/routes/_app.tutor.admin.animations.lab';
import { TutorAdminAnimationAudioPage } from '@/routes/_app.tutor.admin.animations.sons';
import { TutorAdminAnimationTestPage } from '@/routes/_app.tutor.admin.animations.$animationId';
import { TutorAdminAnimationStudioPage } from '@/routes/_app.tutor.admin.animations.$animationId.studio';
import { StudentProgressionPage } from '@/routes/_app.student.progression';
import { StudentSkillcardsPage } from '@/routes/_app.student.skillcards';
import { TutorCardsNewPage } from '@/routes/_app.tutor.cards.new';
import { TutorCardIdeasPage } from '@/routes/_app.tutor.cards.ideas';
import { TutorCardDetailPage } from '@/routes/_app.tutor.cards.$cardId';
import { TutorCardPrintPage } from '@/routes/_app.tutor.cards.$cardId.print';
import { PackRevisionSheetPage } from '@/routes/_app.pack.$packId.fiche';
import { TutorSubmissionsPage } from '@/routes/_app.tutor.submissions';
import { TutorAdminDemoPage } from '@/routes/_app.tutor.admin.demo';
import { TutorEpreuvesPage } from '@/routes/_app.tutor.epreuves';
import { ParentLayout } from '@/routes/_app.parent';
import { ParentDashboardPage } from '@/routes/_app.parent.dashboard';
import { ParentNewsPage } from '@/routes/_app.parent.news';
import { ParentChildrenPage } from '@/routes/_app.parent.children';
import { ParentSubscriptionsPage } from '@/routes/_app.parent.subscriptions';
import { PlayerLayout } from '@/routes/_app.player';
import { PlayerDashboardPage } from '@/routes/_app.player.dashboard';
import { PlayerNewsPage } from '@/routes/_app.player.news';
import { PlayerContactsPage } from '@/routes/_app.player.contacts';
import { PlayerJouerPage } from '@/routes/_app.player.jouer';
import { TutorNewsPage } from '@/routes/_app.tutor.news';
import { ProfilePage } from '@/routes/_app.profile';
import { AccountPrivacyPage } from '@/routes/_app.account.privacy';
import { OnboardingPage } from '@/routes/onboarding';
import { ParentPayPage } from '@/routes/parent-pay';
import { AcheterPage } from '@/routes/acheter';
import { BienvenuePage } from '@/routes/bienvenue';
import { OffertPage } from '@/routes/offert.$token';
import { JoinTutorInvitePage } from '@/routes/rejoindre.$token';
import { ParentalConsentPage } from '@/routes/auth.parental-consent';
import { LiveQuizPage } from '@/routes/live.$niveau';
import { PrivateLivePage } from '@/features/private-live/private-live-page';
import { CreatePrivateLivePage } from '@/features/private-live/create-private-live-page';
import { StudentLayout } from '@/routes/_app.student';
import { StudentDashboardPage } from '@/routes/_app.student.dashboard';
import { StudentObjectivesPage } from '@/routes/_app.student.objectives';
import { ObjectiveDetailPage } from '@/routes/_app.student.objectives.$objectiveId';
import { StudentNewsPage } from '@/routes/_app.student.news';
import { StudentRevisePage } from '@/routes/_app.student.revise';
import { StudentWarmupPage } from '@/routes/_app.student.warmup';
import { StudentEvaluationsPage } from '@/features/evaluations/evaluations-page';
import { AutomatismesPage } from '@/features/automatismes/automatismes-page';
import { PackTestPage } from '@/features/pack-test/pack-test-page';
import { ParentDiagnosticPage } from '@/features/skill-diagnostic/parent-diagnostic-page';
import { ParentSkillcardsPage } from '@/features/skillcards/parent-skillcards-page';
import { GiftsPage } from '@/features/gifts/gifts-page';
import { AdultDiagnosticPage } from '@/features/skill-diagnostic/adult-diagnostic-page';
import { RedeemGiftPage } from '@/features/gifts/redeem-gift-page';
import { TrousseCollectionPage } from '@/features/trousse-collection/collection-page';
import { TrousseGiftPage } from '@/features/trousse-collection/trousse-gift-page';
import { ConnectPage } from '@/features/connect/connect-page';
import { DiagnosticPage, DiagnosticReportPage } from '@/features/skill-diagnostic/diagnostic-page';
import { StudentContactsPage } from '@/routes/_app.student.contacts';
import { StudentFilesPage } from '@/routes/_app.student.files';
import { StudentDiscussionsPage } from '@/routes/_app.student.discussions';
import { StudentReserverCoursPage } from '@/routes/_app.student.rendez-vous';
import { AcceptPeerInvitePage } from '@/routes/_app.student.contacts.accept-peer.$code';
import { AcceptParentLinkPage } from '@/routes/_app.lier-parent.$code';
import { RendezVousJoinPage } from '@/routes/_app.rendez-vous.$reservationId';
import { DiscussionThreadPage } from '@/routes/_app.discussions.$discussionId';
import { AttemptReviewPage } from '@/routes/_app.attempts.$attemptId';
import { SpeakerVoicesPage } from '@/features/animations/speaker-voices-page';
import { LivePrizeChoicePage } from '@/features/live-quiz/live-prize-choice-page';
import { RendezVousRoomPage } from '@/routes/_app.rendez-vous.$reservationId.room';
import { EpreuvePage } from '@/routes/_app.learn.epreuve.$epreuveId';
import { TutorPublicProfilePage } from '@/routes/tutors.$tutorId';
import { ReserverPage } from '@/routes/reserver.$slotId';
import { DemoPage } from '@/routes/demo';
import { DemoExercisePage } from '@/routes/demo.exercise.$exerciseId';
import { ThemesIndexPage } from '@/routes/_app.themes';
import { ThemeDetailPage } from '@/routes/_app.themes.$slug';
import { TutorThemesPage } from '@/routes/_app.tutor.themes';
import { TutorThemeNewPage } from '@/routes/_app.tutor.themes.new';
import { TutorThemeEditPage } from '@/routes/_app.tutor.themes.$id.edit';
import { TutorAdminThemesModerationPage } from '@/routes/_app.tutor.admin.themes.moderation';
import { TutorAdminShowcasePlaylistPage } from '@/routes/_app.tutor.admin.showcase-playlist';
import { TutorAdminHeroPage } from '@/routes/_app.tutor.admin.hero';
import { TutorAdminPipelinePage } from '@/routes/_app.tutor.admin.pipeline';
import { TutorAdminPipelineNewPage } from '@/routes/_app.tutor.admin.pipeline.new';
import { TutorAdminPipelineScenariosPage } from '@/routes/_app.tutor.admin.pipeline.$runId.scenarios';
import { TutorAdminPipelineMatchingsPage } from '@/routes/_app.tutor.admin.pipeline.$runId.matchings';
import { TutorAdminPipelineRunPage } from '@/routes/_app.tutor.admin.pipeline.$runId';
import { TutorAdminPipelineReviewPage } from '@/routes/_app.tutor.admin.pipeline.$runId.review';
import { TutorAdminYoutubePage } from '@/routes/_app.tutor.admin.youtube';
import { TutorAdminYoutubeNewPage } from '@/routes/_app.tutor.admin.youtube.new';
import { TutorAdminYoutubeVideoPage } from '@/routes/_app.tutor.admin.youtube.$videoId';
import { TutorAdminYoutubeConnectPage } from '@/routes/_app.tutor.admin.youtube.connect';
import { TutorAdminChallengesPage } from '@/routes/_app.tutor.admin.challenges';
import { TutorAdminChallengeDiffusionPage } from '@/routes/_app.tutor.admin.challenges.$challengeId';
import { TutorAdminDiffusionsPage } from '@/routes/_app.tutor.admin.diffusions';
import { TutorAdminLivePage } from '@/routes/_app.tutor.admin.live';

function rolePath(
  profile: AuthState['profile'],
  email?: string | null,
): '/tutor/dashboard' | '/parent/dashboard' | '/student/dashboard' | '/player/dashboard' {
  if (profile?.role === 'tutor' || profile?.role === 'admin') return '/tutor/dashboard';
  if (email === 'philippe.hoton@gmail.com') return '/tutor/dashboard';
  if (profile?.role === 'parent') return '/parent/dashboard';
  if (profile?.role === 'player') return '/player/dashboard';
  return '/student/dashboard';
}

/**
 * Où envoyer un utilisateur connecté qui arrive sur `/`, `/login` ou
 * `/onboarding` : d'abord la page publique d'où il s'est connecté (carte
 * SKILLCARD, `lib/return-to`), sinon son tableau de bord. Toutes ces portes
 * doivent passer par ici, sinon le retour est perdu (ex. Google sur le web :
 * `/` → `/onboarding` le temps que le profil charge → tableau de bord).
 */
function afterAuthRedirect(profile: AuthState['profile'], email?: string | null): never {
  const back = consumeReturnTo();
  if (back) throw redirect({ href: back });
  throw redirect({ to: rolePath(profile, email) });
}

interface RouterContext {
  auth: AuthState;
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

// Le RouterProvider n'est monté qu'après loading=false (voir main.tsx).
// beforeLoad voit donc toujours un état stable et peut rediriger immédiatement.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const { session, profile, user } = context.auth;
    // Premier lancement : on présente l'app avant de demander de se connecter.
    if (!session) throw redirect({ to: hasSeenDecouverte() ? '/login' : '/decouverte' });
    if (!profile) throw redirect({ to: '/onboarding' });
    // Retour vers la page publique d'où l'utilisateur s'est connecté (ex. carte
    // scannée, connexion OAuth qui revient toujours sur `/`).
    afterAuthRedirect(profile, user?.email);
  },
  component: () => null,
});

// Carrousel de présentation, AVANT le login, affiché une seule fois.
// Public : aucune garde d'auth, comme /acheter ou /demo.
// ≠ /onboarding (création de profil, authentifié) et ≠ /bienvenue (retour Stripe).
const decouverteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/decouverte',
  component: DecouvertePage,
});

// Exercice tutoriel de la feuille de brouillon : public (rien n'est enregistré).
// `?niveau=4e` : classe de l'exercice guidé ; `?apres=tableau|diagnostic` : fin de l'inscription.
const scratchTutorialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/brouillon/tutoriel',
  validateSearch: parseTutorialSearch,
  component: function ScratchTutorialRouteView() {
    const { niveau, apres } = scratchTutorialRoute.useSearch();
    return <ScratchTutorialPage niveau={niveau} apres={apres} />;
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // `?inscription=1` : on arrive de l'accueil `/decouverte` (« Créer mon compte ») —
  // en-tête d'inscription adapté au profil choisi. Sans : connexion.
  validateSearch: (search: Record<string, unknown>): { inscription?: 1 } => ({
    inscription: search.inscription === 1 || search.inscription === '1' ? 1 : undefined,
  }),
  beforeLoad: ({ context }) => {
    const { loading, session, profile } = context.auth;
    if (!loading && session) {
      if (!profile) throw redirect({ to: '/onboarding' });
      afterAuthRedirect(profile, session.user.email);
    }
    // Navigateur / app qui n'a jamais vu la présentation : on la montre d'abord (ses boutons
    // « Créer mon compte » / « J'ai déjà un compte » posent le flag et reviennent ici).
    // Sauf code de connexion en attente : l'élève revient de sa boîte mail, il doit retrouver son code.
    if (!loading && !session && shouldShowDecouverteBeforeLogin())
      throw redirect({ to: '/decouverte' });
  },
  component: function LoginRouteView() {
    const { inscription } = loginRoute.useSearch();
    return <LoginPage signup={inscription === 1} />;
  },
});

// Route /onboarding : accessible à tout utilisateur authentifié,
// sans restriction sur le profil (le composant gère son propre état).
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: ({ context, location }) => {
    if (context.auth.loading) return;
    if (!context.auth.session) throw redirect({ to: '/login' });
    const { profile } = context.auth;
    // Un compte Curieux qui change de type (« Mon profil ») reste ici.
    if (profile && isAccountTypeChange(location.search as Record<string, unknown>, profile.role))
      return;
    if (profile) afterAuthRedirect(profile, context.auth.user?.email);
  },
  component: OnboardingPage,
});

// Routes applicatives : session requise.
// Le guard profil (redirect vers /onboarding si profil null) est dans AppLayout.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context }) => {
    if (!context.auth.loading && !context.auth.session) {
      throw redirect({ to: '/login' });
    }
  },
  component: AppLayout,
});

const learnRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/learn/$subject/$level/$chapter/$exercise',
  component: ExercisePage,
});

// Lecteur d'exercice sans chapitre — exercices d'épreuve (générés par le
// pipeline PDF, `chapter_id = null`). Le player se résout par `exercise` id ;
// subject/level/chapter ne servent qu'au partage/cover et sont optionnels.
const learnExerciseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/learn/exercise/$exercise',
  component: ExercisePage,
});

const learnEpreuveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/learn/epreuve/$epreuveId',
  component: EpreuvePage,
});

const dagPlayerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dag/$exerciseId',
  component: DagPlayerPage,
});

// Lecteur d'article de blog in-app (fil d'actu, migration 0220) — monté
// directement sous `appRoute` (comme dagPlayerRoute), accessible à tous les
// rôles authentifiés sans duplication, et sans collision avec l'authoring
// tuteur existant à `/tutor/blog/*`.
const blogArticleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/blog/$slug',
  component: BlogArticleReaderPage,
});

const tutorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tutor',
  beforeLoad: ({ location }) => {
    if (location.pathname === '/tutor') {
      throw redirect({ to: '/tutor/dashboard' });
    }
  },
  component: TutorLayout,
});

const tutorDashboardRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/dashboard',
  component: TutorDashboardPage,
});

// Abonnement Prof (migration 0691) : essai, souscription, gestion.
const tutorSubscriptionRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/abonnement',
  component: TutorSubscriptionPage,
});

const tutorClassesRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/classes',
  component: TutorClassesPage,
});

const tutorClassNewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/classes/new',
  component: TutorClassNewPage,
});

const tutorClassDetailRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/classes/$classId',
  component: TutorClassDetailPage,
});

// Progression de l'année d'une classe : packs réordonnables + grille (lot F, 0289).
const tutorClassProgressionRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/classes/$classId/progression',
  component: TutorClassProgressionPage,
});

const tutorStudentsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/students',
  // Doublon de la liste « Mes élèves » de /tutor/contacts (même lien, même
  // invitation) : redirigé. Le retrait d'un élève vit désormais sur sa fiche.
  beforeLoad: () => {
    throw redirect({ to: '/tutor/contacts' });
  },
});

const tutorContactsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/contacts',
  component: TutorContactsPage,
});

const tutorFilesRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/files',
  component: TutorFilesPage,
});

const tutorDiscussionsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/discussions',
  component: TutorDiscussionsPage,
});

const tutorBookingDashboardRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/rendez-vous',
  component: TutorBookingDashboardPage,
});

const tutorBookingSlotsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/rendez-vous/creneaux',
  component: TutorBookingSlotsPage,
});

const tutorVisioSeriesRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/rendez-vous/series',
  component: TutorVisioSeriesPage,
});

const tutorBookingPaymentsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/rendez-vous/paiements',
  component: TutorBookingPaymentsPage,
});

// Réunion libre (Phase D, migration 0259) : aucune réservation ni élève
// choisi — l'hôte crée la salle (`create_visio_meeting`) et partage un lien
// (`create_visio_invite`). Route séparée de `TYPE_CARDS`/`RendezVousKind`
// (express/entretien/cours/etude, tous adossés à `create_direct_rendezvous`).
const tutorNewMeetingRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/rendez-vous/reunion',
  component: TutorNewMeetingPage,
});

const tutorBookingNewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/rendez-vous/new',
  component: TutorNewRendezVousPage,
  // Préremplissage depuis la fiche élève ("Nouveau rendez-vous") ou la carte
  // d'un étudiant alternant (section "Mes étudiants" de /tutor/contacts).
  validateSearch: (search: Record<string, unknown>): { kind?: string; studentId?: string } => ({
    kind: typeof search.kind === 'string' ? search.kind : undefined,
    studentId: typeof search.studentId === 'string' ? search.studentId : undefined,
  }),
});

const tutorStudentDetailRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/students/$studentId',
  component: TutorStudentDetailPage,
});

const tutorExercisesRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/exercises',
  component: TutorExercisesPage,
});

const tutorEpreuvesRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/epreuves',
  component: TutorEpreuvesPage,
});

const tutorExercisesNewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/exercises/new',
  component: TutorExercisesNewPage,
  validateSearch: (search: Record<string, unknown>): { chapterId?: string; lessonId?: string } => ({
    chapterId: typeof search.chapterId === 'string' ? search.chapterId : undefined,
    lessonId: typeof search.lessonId === 'string' ? search.lessonId : undefined,
  }),
});

const tutorExercisesNewV2Route = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/exercises/new-v2',
  component: TutorExercisesNewV2Page,
});

const tutorExerciseDetailRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/exercises/$exerciseId',
  component: TutorExerciseDetailPage,
});

const tutorExerciseEditRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/exercises/$exerciseId/edit',
  // Zéro V1 : l'éditeur V1 est retiré, tout exercice s'édite en V2.
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/tutor/exercises/$exerciseId/edit-v2',
      params: { exerciseId: params.exerciseId },
    });
  },
});

const tutorExerciseEditV2Route = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/exercises/$exerciseId/edit-v2',
  component: TutorExerciseEditV2Page,
  // ?question=<bloc> : ouvre l'éditeur SUR cette question ; ?report=<id> :
  // signalement à traiter (migration 0348) — bandeau + révision par l'IA.
  validateSearch: (search: Record<string, unknown>): { question?: string; report?: string } => {
    const id = (v: unknown) =>
      typeof v === 'string' && /^[0-9a-f-]{36}$/i.test(v) ? v : undefined;
    return { question: id(search.question), report: id(search.report) };
  },
});

const tutorExerciseTestRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/exercises/$exerciseId/test',
  component: TutorExerciseTestPage,
});

const tutorSettingsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/settings',
  // Fusionnés dans « Mon profil » (même clé API, même déconnexion).
  beforeLoad: () => {
    throw redirect({ to: '/profile' });
  },
});

// ── Thèmes ─────────────────────────────────────────────────────────────────
const themesIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/themes',
  component: ThemesIndexPage,
});

const themeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/themes/$slug',
  component: ThemeDetailPage,
});

const tutorThemesRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/themes',
  component: TutorThemesPage,
});

const tutorThemeNewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/themes/new',
  component: TutorThemeNewPage,
});

const tutorThemeEditRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/themes/$id/edit',
  component: TutorThemeEditPage,
});

const tutorAdminRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/admin',
  component: TutorAdminLayout,
});

const tutorAdminChaptersRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/chapters',
  component: TutorAdminChaptersPage,
});

const tutorAdminChaptersNewRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/chapters/new',
  component: TutorAdminChaptersNewPage,
});

const tutorAdminChapterDetailRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/chapters/$chapterId',
  component: TutorAdminChapterDetailPage,
});

const tutorAdminChapterEditRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/chapters/$chapterId/edit',
  component: TutorAdminChapterEditPage,
});

const tutorAdminLessonDetailRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/lessons/$lessonId',
  component: TutorAdminLessonDetailPage,
});

const tutorAdminLessonEditRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/lessons/$lessonId/edit',
  component: TutorAdminLessonEditPage,
});

const tutorAdminLessonEditV2Route = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/lessons/$lessonId/edit-v2',
  component: TutorAdminLessonEditV2Page,
});

const tutorAdminCompetenceDetailRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/competences/$competenceId',
  component: TutorAdminCompetenceEditorPage,
});

const tutorAdminExercisesRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/exercises',
  beforeLoad: () => {
    throw redirect({ to: '/tutor/exercises' });
  },
});

const tutorAdminExercisesNewRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/exercises/new',
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/tutor/exercises/new', search });
  },
  validateSearch: (search: Record<string, unknown>): { chapterId?: string; lessonId?: string } => ({
    chapterId: typeof search.chapterId === 'string' ? search.chapterId : undefined,
    lessonId: typeof search.lessonId === 'string' ? search.lessonId : undefined,
  }),
});

const tutorAdminExerciseDetailRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/exercises/$exerciseId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/tutor/exercises/$exerciseId',
      params: { exerciseId: params.exerciseId },
    });
  },
});

const tutorAdminExerciseEditRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/exercises/$exerciseId/edit',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/tutor/exercises/$exerciseId/edit-v2',
      params: { exerciseId: params.exerciseId },
    });
  },
});

const tutorAdminPromptsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/prompts',
  component: TutorAdminPromptsPage,
});

const tutorAdminObjectivesRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/objectives',
  component: TutorAdminObjectivesPage,
});

const tutorAdminFaqRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/faq',
  component: TutorAdminFaqPage,
});

// Tarifs des SKILLCARDS (migration 0304) : grille, prix forcés, derniers achats.
const tutorAdminSkillcardPricingRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/skillcards-pricing',
  component: TutorAdminSkillcardPricingPage,
});

// Utilisateurs (migration 0306) : liste, accès, pass offerts + notification.
const tutorAdminUsersRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/users',
  component: TutorAdminUsersPage,
});

const tutorAdminAlwaysdataRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/alwaysdata',
  component: TutorAdminAlwaysdataPage,
});

const tutorAdminObjectivesNewRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/objectives/new',
  component: TutorAdminObjectivesNewPage,
});

const tutorAdminObjectiveDetailRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/objectives/$objectiveId',
  component: TutorAdminObjectiveDetailPage,
});

// Packs de la progression type (lot F, migration 0289).
const tutorAdminPacksRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/packs',
  component: TutorAdminPacksPage,
});

// Boîte à outils : catalogue des outils, test, SKILLCARDS reliées (`card_tools`, 0551).
const tutorAdminToolboxRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/toolbox',
  component: TutorAdminToolboxPage,
});

// Trousses : les 32 modèles du catalogue en 3D, ouverts / fermés au double clic.
const tutorAdminTroussesRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/trousses',
  component: TutorAdminTroussesPage,
});

// Animations (moteur JRAF) : rubrique « Animations SKILLCARDS » (liste, régénérer,
// supprimer, publier), banc du lecteur sur l'animation de référence (lot 2),
// « Tester » le brouillon créé par l'IA depuis une SKILLCARD (lot 3).
const tutorAdminAnimationsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/animations',
  component: TutorAdminAnimationsPage,
});

const tutorAdminAnimationLabRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/animations/lab',
  component: TutorAdminAnimationLabPage,
});

const tutorAdminAnimationTestRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/animations/$animationId',
  component: TutorAdminAnimationTestPage,
});

// Studio : modifier le brouillon par formulaires, aperçu au curseur de temps (lot 5)
const tutorAdminAnimationStudioRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/animations/$animationId/studio',
  component: TutorAdminAnimationStudioPage,
});

// Sons des animations : musique d'ambiance et jingles (bibliothèque audio, 0669)
const tutorAdminAnimationAudioRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/animations/sons',
  component: TutorAdminAnimationAudioPage,
});

const tutorAdminEpreuvesRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/epreuves',
  component: TutorAdminEpreuvesPage,
});

const tutorAdminEpreuvesNewRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/epreuves/new',
  component: TutorAdminEpreuvesNewPage,
});

const tutorAdminEpreuveDetailRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/epreuves/$epreuveId',
  component: TutorAdminEpreuveDetailPage,
});

// Blog réflexif : désormais ouvert aux TUTEURS (sous /tutor, plus /tutor/admin).
const tutorBlogRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/blog',
  component: TutorBlogPage,
});

const tutorBlogNewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/blog/new',
  component: TutorBlogNewPage,
});

const tutorBlogDetailRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/blog/$articleId',
  component: TutorBlogDetailPage,
});

const tutorBlogStatsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/blog/$articleId/stats',
  component: TutorBlogStatsPage,
});

// Modération des soumissions tuteurs → RESTE admin-only (sous /tutor/admin).
const tutorAdminBlogModerationRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/blog/moderation',
  component: TutorAdminBlogModerationPage,
});

// Signalements (messages / utilisateurs) — admin-only, migration 0604.
const tutorAdminReportsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/reports',
  component: TutorAdminReportsPage,
});

// Statistiques en direct (visiteurs, pages, courbes) — admin-only, migration 0697.
const tutorAdminStatsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/stats',
  component: TutorAdminStatsPage,
});

// Avis sur la feuille de brouillon (étoiles, idées, problèmes) — admin-only, migration 0696.
const tutorAdminScratchFeedbackRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/brouillon-avis',
  component: TutorAdminScratchFeedbackPage,
});

// Demandes de test de l'app Android (test fermé Google Play) — admin-only, migration 0732.
const tutorAdminAndroidTestersRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/testeurs-android',
  component: TutorAdminAndroidTestersPage,
});

const tutorAdminSocialConnectedRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/social/connected',
  component: TutorAdminSocialConnectedPage,
});

const tutorAdminSocialSettingsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/social/settings',
  component: TutorAdminSocialSettingsPage,
});

const tutorAdminConnectionsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/connexions',
  component: TutorAdminConnectionsPage,
});

const tutorAdminSchoolsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/etablissements',
  component: TutorAdminSchoolsPage,
});

const tutorAdminNewsletterRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/newsletter',
  component: TutorAdminNewsletterPage,
  // Filtre optionnel : répondants au sondage d'un article (article + bloc).
  validateSearch: (search: Record<string, unknown>): { article?: string; block?: number } => {
    const article =
      typeof search.article === 'string' && search.article ? search.article : undefined;
    let block: number | undefined;
    if (typeof search.block === 'number' && Number.isInteger(search.block)) {
      block = search.block;
    } else if (typeof search.block === 'string' && search.block !== '') {
      const n = Number(search.block);
      if (Number.isInteger(n)) block = n;
    }
    return { ...(article ? { article } : {}), ...(block !== undefined ? { block } : {}) };
  },
});

const tutorAdminDagRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/dag',
  component: TutorAdminDagPage,
});

// Générateur paramétrique d'exercices (Blueprint) : ouvert aux TUTEURS (sous
// /tutor, plus /tutor/admin) — le backend (RLS + edge fn) accepte déjà
// role in ('tutor','admin'), seul le routing/nav restreignait à l'admin.
const tutorBlueprintsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/blueprints',
  component: TutorBlueprintsPage,
});

const tutorBlueprintsNewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/blueprints/new',
  component: TutorBlueprintsNewPage,
  // ?submission=<id> : préremplit le formulaire depuis une soumission élève
  // (boîte de réception /tutor/submissions, migration 0222).
  validateSearch: (search: Record<string, unknown>): { submission?: string } => ({
    submission: typeof search.submission === 'string' ? search.submission : undefined,
  }),
});

const tutorBlueprintDetailRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/blueprints/$blueprintId',
  component: TutorBlueprintDetailPage,
});

// SKILLCARDS pédagogiques (cartes 63 × 88 + QR, migrations 0269-0271) : tuteurs et admins.
const tutorCardsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/cards',
  component: TutorCardsPage,
});

const tutorCardsNewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/cards/new',
  component: TutorCardsNewPage,
  // ?parent= : « Créer une carte dérivée » depuis une carte (filiation, migration 0275).
  // ?title=&level=&subject=&topic= : carte « à créer » d'un pack (lot F, migration 0289).
  validateSearch: (
    search: Record<string, unknown>,
  ): { parent?: string; title?: string; level?: string; subject?: string; topic?: string } => {
    const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.slice(0, 200) : undefined);
    return {
      parent: str(search.parent),
      title: str(search.title),
      level: str(search.level),
      subject: str(search.subject),
      topic: str(search.topic),
    };
  },
});

// Rapport d'un diagnostic depuis le Feed « tout le monde » de l'admin (lecture seule ;
// RLS / RPC : admin autorisé). Même page que côté parent.
const tutorDiagnosticRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/diagnostic/$diagnosticId',
  component: function TutorDiagnosticRoute() {
    const { diagnosticId } = tutorDiagnosticRoute.useParams();
    return <ParentDiagnosticPage diagnosticId={diagnosticId} backTo="/tutor/news" />;
  },
});

// Idées de SKILLCARDS proposées par l'IA (admin) : niveau + rareté + thème → cartes.
const tutorCardIdeasRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/cards/ideas',
  component: TutorCardIdeasPage,
});

const tutorCardDetailRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/cards/$cardId',
  component: TutorCardDetailPage,
  // ?generationError= : la génération IA lancée depuis /new a échoué (la carte existe).
  validateSearch: (search: Record<string, unknown>): { generationError?: string } => ({
    generationError:
      typeof search.generationError === 'string' ? search.generationError : undefined,
  }),
});

const tutorSubmissionsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/submissions',
  component: TutorSubmissionsPage,
});

const tutorAdminDemoRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/demo',
  component: TutorAdminDemoPage,
});

const tutorAdminThemesModerationRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/themes/moderation',
  component: TutorAdminThemesModerationPage,
});

const tutorAdminShowcasePlaylistRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/showcase-playlist',
  component: TutorAdminShowcasePlaylistPage,
});

const tutorAdminHeroRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/hero',
  component: TutorAdminHeroPage,
});

const tutorAdminPipelineRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/pipeline',
  component: TutorAdminPipelinePage,
});

const tutorAdminPipelineNewRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/pipeline/new',
  component: TutorAdminPipelineNewPage,
});

const tutorAdminPipelineRunRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/pipeline/$runId',
  component: TutorAdminPipelineRunPage,
});

const tutorAdminPipelineReviewRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/pipeline/$runId/review',
  component: TutorAdminPipelineReviewPage,
});

const tutorAdminPipelineScenariosRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/pipeline/$runId/scenarios',
  component: TutorAdminPipelineScenariosPage,
});

const tutorAdminPipelineMatchingsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/pipeline/$runId/matchings',
  component: TutorAdminPipelineMatchingsPage,
});

const tutorAdminYoutubeRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/youtube',
  component: TutorAdminYoutubePage,
});

const tutorAdminYoutubeNewRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/youtube/new',
  component: TutorAdminYoutubeNewPage,
});

const tutorAdminYoutubeConnectRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/youtube/connect',
  component: TutorAdminYoutubeConnectPage,
});

const tutorAdminYoutubeVideoRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/youtube/$videoId',
  component: TutorAdminYoutubeVideoPage,
});

const tutorAdminChallengesRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/challenges',
  component: TutorAdminChallengesPage,
});

const tutorAdminChallengeRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/challenges/$challengeId',
  component: TutorAdminChallengeDiffusionPage,
});

const tutorAdminDiffusionsRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/diffusions',
  component: TutorAdminDiffusionsPage,
});

const tutorAdminLiveRoute = createRoute({
  getParentRoute: () => tutorAdminRoute,
  path: '/live',
  component: TutorAdminLivePage,
});

const parentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/parent',
  beforeLoad: ({ location }) => {
    if (location.pathname === '/parent') {
      throw redirect({ to: '/parent/dashboard' });
    }
  },
  component: ParentLayout,
});

const parentDashboardRoute = createRoute({
  getParentRoute: () => parentRoute,
  path: '/dashboard',
  component: ParentDashboardPage,
});

const parentNewsRoute = createRoute({
  getParentRoute: () => parentRoute,
  path: '/news',
  component: ParentNewsPage,
});

// Rapport du diagnostic d'un enfant (migration 0325).
const parentDiagnosticRoute = createRoute({
  getParentRoute: () => parentRoute,
  path: '/diagnostic/$diagnosticId',
  component: function ParentDiagnosticRoute() {
    const { diagnosticId } = parentDiagnosticRoute.useParams();
    return <ParentDiagnosticPage diagnosticId={diagnosticId} />;
  },
});

const parentChildrenRoute = createRoute({
  getParentRoute: () => parentRoute,
  path: '/children',
  // `?add=create|link` : ouvre directement l'ajout d'un enfant (depuis l'Accueil).
  validateSearch: (search: Record<string, unknown>): { add?: 'create' | 'link' } => ({
    add: search.add === 'create' || search.add === 'link' ? search.add : undefined,
  }),
  component: ParentChildrenPage,
});

// Ancienne page « Mon réseau » (doublon de « Mes enfants », hors navigation) :
// les vieux liens atterrissent sur « Mes enfants ».
const parentContactsRoute = createRoute({
  getParentRoute: () => parentRoute,
  path: '/contacts',
  beforeLoad: () => {
    throw redirect({ to: '/parent/children' });
  },
});

const parentSubscriptionsRoute = createRoute({
  getParentRoute: () => parentRoute,
  path: '/subscriptions',
  component: ParentSubscriptionsPage,
});

const playerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/player',
  beforeLoad: ({ location }) => {
    if (location.pathname === '/player') {
      throw redirect({ to: '/player/dashboard' });
    }
  },
  component: PlayerLayout,
});

const playerDashboardRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '/dashboard',
  component: PlayerDashboardPage,
});

const playerNewsRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '/news',
  component: PlayerNewsPage,
});

const playerContactsRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '/contacts',
  component: PlayerContactsPage,
});

// « Mes SKILLCARDS » du joueur : même page que l'élève (RPC « soi-même »).
const playerSkillcardsRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '/skillcards',
  component: () => <StudentSkillcardsPage dashboardPath="/player/dashboard" />,
});

const playerJouerRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '/jouer',
  component: PlayerJouerPage,
});

const tutorNewsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/news',
  component: TutorNewsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  component: ProfilePage,
});

const accountPrivacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account/privacy',
  component: AccountPrivacyPage,
});

// Cible des liens / QR de liaison parent ⇄ enfant — accessible par n'importe
// quel compte authentifié (parent OU élève), l'orientation est déduite côté RPC.
const linkParentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lier-parent/$code',
  component: AcceptParentLinkPage,
});

const studentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/student',
  component: StudentLayout,
});

const studentDashboardRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/dashboard',
  component: StudentDashboardPage,
});

const studentObjectivesRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/objectives',
  component: StudentObjectivesPage,
});

const studentObjectiveDetailRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/objectives/$objectiveId',
  component: ObjectiveDetailPage,
});

const studentNewsRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/news',
  component: StudentNewsPage,
});

const studentReviseRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/revise',
  component: StudentRevisePage,
});

const studentWarmupRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/warmup',
  component: StudentWarmupPage,
});

// Diagnostic de programme (SKILLCARDS, migration 0318).
const studentDiagnosticRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/diagnostic',
  // `?start=1` : lancé directement (fin de l'onboarding élève).
  validateSearch: (search: Record<string, unknown>): { start?: 1 } => ({
    start: search.start === 1 || search.start === '1' ? 1 : undefined,
  }),
  component: function DiagnosticRoute() {
    const { start } = studentDiagnosticRoute.useSearch();
    return <DiagnosticPage autoStart={start === 1} />;
  },
});

// Rapport (partiel dès 5 réponses) d'un diagnostic — migration 0320.
const studentDiagnosticReportRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/diagnostic/rapport/$diagnosticId',
  component: function DiagnosticReportRoute() {
    const { diagnosticId } = studentDiagnosticReportRoute.useParams();
    return <DiagnosticReportPage diagnosticId={diagnosticId} />;
  },
});

const studentContactsRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/contacts',
  component: StudentContactsPage,
});

const studentFilesRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/files',
  component: StudentFilesPage,
});

// « Mon année » : frise de la progression type du niveau (lot F, migration 0289).
const studentProgressionRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/progression',
  component: StudentProgressionPage,
});

// « Se tester » sur un pack de « Mon année » (payant, migration 0336).
const studentPackTestRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/progression/test/$packId',
  component: PackTestPage,
});

// Automatismes : séries de questions flash, XP + HORA (migration 0323).
const studentAutomatismesRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/automatismes',
  component: AutomatismesPage,
});

// « Mes notes et évals » : évals programmées, notes, moyenne (migration 0321).
const studentEvaluationsRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/evaluations',
  component: StudentEvaluationsPage,
});

// « Mes SKILLCARDS » : statistiques + toutes les cartes (migration 0277).
const studentSkillcardsRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/skillcards',
  component: StudentSkillcardsPage,
});

const studentDiscussionsRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/discussions',
  component: StudentDiscussionsPage,
});

const studentReserverCoursRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/rendez-vous',
  component: StudentReserverCoursPage,
});

// Cible des liens/notifications de rendez-vous (cours/express/étude/entretien)
// — accessible tuteur ET élève, l'un ou l'autre participant de la réservation
// (RLS tutor_booking_reservations : own_select / tutor_select).
// Impression 63 × 88 d'une carte : enfant direct de appRoute (hors layout tuteur →
// aucune barre latérale sur le PDF). Accès garanti par la RLS de `cards`.
const tutorCardPrintRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tutor/cards/$cardId/print',
  component: TutorCardPrintPage,
});

// Fiche de révision d'un pack (migration 0723) : compilation des SKILLCARDS du pack, lecture
// + impression A4. Enfant direct de appRoute (hors layout → aucune barre latérale sur le PDF).
// Le contenu est filtré par `get_pack_revision_sheet` ; `?student=` pour un tuteur lié / admin.
const packRevisionSheetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pack/$packId/fiche',
  validateSearch: (search: Record<string, unknown>): { student?: string } => ({
    student: typeof search.student === 'string' ? search.student : undefined,
  }),
  component: PackRevisionSheetPage,
});

const rendezVousJoinRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rendez-vous/$reservationId',
  component: RendezVousJoinPage,
});

// Fil d'une discussion asynchrone (migrations 0265-0267) — accessible tuteur ET
// élève participant, RLS `discussions: select_participant`.
const discussionThreadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/discussions/$discussionId',
  component: DiscussionThreadPage,
});

// Résultats d'un essai question par question (Feed → carte « a terminé un
// exercice », migration 0272). Tous rôles : la RPC `get_attempt_review` tranche.
const attemptReviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/attempts/$attemptId',
  component: AttemptReviewPage,
});

// « Audios animations » (permission `speaker`, migration 0678) : tous rôles ;
// la page et les edge fns vérifient la permission.
const speakerVoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audios-animations',
  component: SpeakerVoicesPage,
});

// Lot du Live School à choisir (migration 0331) : le gagnant possédait déjà la
// SKILLCARD mise en jeu. La RPC `live_prize_choice_cards` tranche.
const livePrizeChoiceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lot/$challengeId',
  component: function LivePrizeChoiceRoute() {
    const { challengeId } = livePrizeChoiceRoute.useParams();
    return <LivePrizeChoicePage challengeId={challengeId} />;
  },
});

// Salle vidéo NATIVE (même origine) — cf. useJoinVisioRoom : Capacitor
// navigue ici plutôt que d'ouvrir tutor.jeraisonne.fr dans un navigateur
// externe. Web n'y navigue jamais.
const rendezVousRoomRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rendez-vous/$reservationId/room',
  component: RendezVousRoomPage,
});

// Salle vidéo native d'une RÉUNION LIBRE (Phase D, migration 0259) — l'hôte
// (déjà admis par `create_visio_meeting`) rejoint directement, sans RPC de
// résolution. Un invité qui suit le lien passe par `apps/tutor` (`/invite/$token`),
// pas par cette route.
const freeMeetingRoomRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reunion/$roomId/room',
  component: FreeMeetingRoomPage,
});

const studentAcceptPeerRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/contacts/accept-peer/$code',
  component: AcceptPeerInvitePage,
});

const parentPayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parent-pay',
  component: ParentPayPage,
});

// Achat direct depuis le marketing — redirige vers Stripe (sans compte).
const acheterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acheter',
  component: AcheterPage,
});

// Retour Stripe après achat — bandeau « tu viens d'acheter X » + connexion OTP.
const bienvenueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bienvenue',
  component: BienvenuePage,
});

// SKILLCARD : cible PERMANENTE du QR imprimé au verso (migration 0269).
// Publique (enfant direct de rootRoute) : la carte se découvre sans compte,
// la progression demande une connexion faite sur la page elle-même.
const publicCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/card/$cardId',
  // `?defi=<jeton>` : défi partagé (migration 0285), lu par la page.
  validateSearch: (search: Record<string, unknown>): { defi?: string } => ({
    defi: typeof search.defi === 'string' ? search.defi : undefined,
  }),
  component: PublicCardPage,
});

// Animation publiée (lot 4, migration 0666) : lien de partage et des Shorts.
// Publique : la partie gratuite se joue sans compte ; la partie « À toi »
// n'est servie qu'avec l'accès à la SKILLCARD source.
const publicAnimationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/animation/$slug',
  component: PublicAnimationPage,
});

// Vidéo d'une animation (lot 7) : rendu image par image piloté par le worker
// vidéo (Playwright). Publique, sans données : le document est injecté.
const captureAnimationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/capture/animation',
  component: CaptureAnimationPage,
});

// Achat d'une SKILLCARD (migration 0304) : carte / pack / programme. Publique
// (prix visibles sans compte, connexion sur la page). `?return=` = chemin
// interne où revenir (exercice en cours), `?purchase=` = retour de Stripe.
const skillcardBuyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/card/$cardId/acheter',
  validateSearch: (search: Record<string, unknown>): { return?: string; purchase?: string } => ({
    return: typeof search.return === 'string' ? search.return : undefined,
    purchase: typeof search.purchase === 'string' ? search.purchase : undefined,
  }),
  component: SkillcardBuyPage,
});

// Achat d'un PACK de SKILLCARDS (migration 0337) : le pack entier ou son
// programme. Publique comme la page d'une carte ; `?return=` / `?purchase=`.
const packBuyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pack/$packId/acheter',
  validateSearch: (search: Record<string, unknown>): { return?: string; purchase?: string } => ({
    return: typeof search.return === 'string' ? search.return : undefined,
    purchase: typeof search.purchase === 'string' ? search.purchase : undefined,
  }),
  component: PackBuyPage,
});

// Achat EXPRESS d'un pack / d'un programme depuis le site : connexion sur
// place puis paiement direct (publique).
const expressBuyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/debloquer/$offer/$packId',
  component: ExpressBuyPage,
});

// « Faire payer par un proche » (migration 0312) : page publique du payeur,
// QR code d'un profil : se relier (publique, migration 0334).
const connectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion/$code',
  component: function ConnectRoute() {
    const { code } = connectRoute.useParams();
    return <ConnectPage code={code} />;
  },
});

// Carte cadeau SKILLCARDS : activation (publique, migration 0326).
const redeemGiftRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cadeau/$code',
  component: function RedeemGiftRoute() {
    const { code } = redeemGiftRoute.useParams();
    return <RedeemGiftPage code={code} />;
  },
});

// Trousse offerte : la recevoir (publique, migration 0738).
const trousseGiftRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trousse/$token',
  component: function TrousseGiftRoute() {
    const { token } = trousseGiftRoute.useParams();
    return <TrousseGiftPage token={token} />;
  },
});

// « Mes trousses » : la collection, la roue du jour, les cadeaux (migration 0738).
const trousseCollectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/trousses',
  component: TrousseCollectionPage,
});

// « Veux-tu t'évaluer ? » : bilan de niveau d'un adulte (migration 0328).
const selfAssessmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/evaluer',
  component: AdultDiagnosticPage,
});

// « Mes cadeaux » : codes achetés (migration 0326). `?purchase=` = retour Stripe.
const giftsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cadeaux',
  validateSearch: (search: Record<string, unknown>): { purchase?: string } => ({
    purchase: typeof search.purchase === 'string' ? search.purchase : undefined,
  }),
  component: GiftsPage,
});

// Parent : SKILLCARDS achetées pour ses enfants (migration 0326).
const parentSkillcardsRoute = createRoute({
  getParentRoute: () => parentRoute,
  path: '/skillcards',
  validateSearch: (search: Record<string, unknown>): { child?: string; purchase?: string } => ({
    child: typeof search.child === 'string' ? search.child : undefined,
    purchase: typeof search.purchase === 'string' ? search.purchase : undefined,
  }),
  component: ParentSkillcardsPage,
});

// sans compte. `?paid=1` = retour de Stripe.
const payForStudentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payer/$token',
  validateSearch: (search: Record<string, unknown>): { paid?: string } => ({
    paid: typeof search.paid === 'string' ? search.paid : undefined,
  }),
  component: PayForStudentPage,
});

// URL courte de secours imprimée au verso (`/c/ABC234` → `/card/{id}`).
const cardShortCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/$code',
  component: CardShortCodePage,
});

// Réclamation d'un accès offert (handoff acheteur → enfant).
const offertRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offert/$token',
  component: OffertPage,
});

// Lien d'invitation tuteur (migration 0241) — accessible sans compte : sign-
// up/login puis liaison automatique au tuteur (tutor_links ou academic_tutor_id).
const rejoindreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rejoindre/$token',
  component: JoinTutorInvitePage,
});

// Accord parental (RGPD art.8) — le parent clique le lien magique du mail.
// Public : pas de compte requis (PUBLIC_ROUTES.parentalConsent).
const parentalConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/parental-consent',
  component: ParentalConsentPage,
});

// Fiche publique d'un tuteur — accessible sans authentification.
const tutorPublicProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tutors/$tutorId',
  component: TutorPublicProfilePage,
});

// Réservation d'un cours particulier (style Calendly) — accessible sans compte
// depuis la fiche publique marketing d'un tuteur (le compte élève est créé au
// paiement si besoin, cf. tutor-booking-checkout + stripe-webhook).
const reserverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reserver/$slotId',
  component: ReserverPage,
});

// Démo guidée anonyme — accessible sans compte (4 phases côté composant).
const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/demo',
  component: DemoPage,
});

// Aperçu anonyme d'un exercice précis — bouton « Tester » des cartes marketing.
const demoExerciseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/demo/exercise/$exerciseId',
  component: DemoExercisePage,
});

// Live School (live synchrone) — page publique par niveau (/live/<niveau>).
// Gère l'inscription inline (niveau pré-défini) + la garde « son niveau uniquement ».
const liveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/live/$niveau',
  // `?defi=<id>` : un live précis (Live School du dimanche, plusieurs par niveau) ;
  // `?test=<id>` : mode test staff.
  validateSearch: (search: Record<string, unknown>): { defi?: string; test?: string } => ({
    defi: typeof search.defi === 'string' ? search.defi : undefined,
    test: typeof search.test === 'string' ? search.test : undefined,
  }),
  component: LiveQuizPage,
});

// Défi live privé (migration 0650) : page publique par code (invité ou compte).
const privateLiveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/defi-live/$code',
  component: function PrivateLiveRoute() {
    const { code } = privateLiveRoute.useParams();
    return <PrivateLivePage code={code} />;
  },
});

// Créer un défi live privé (compte connecté).
const createPrivateLiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/live/prive/nouveau',
  // `?from=<code>` : « Prendre ma revanche » reprend les réglages du défi.
  // `?exercise=<id>&card=<id>` : « En faire un défi » depuis un exercice (0722).
  validateSearch: (
    search: Record<string, unknown>,
  ): { from?: string; exercise?: string; card?: string } => ({
    from: typeof search.from === 'string' ? search.from : undefined,
    exercise: typeof search.exercise === 'string' ? search.exercise : undefined,
    card: typeof search.card === 'string' ? search.card : undefined,
  }),
  component: function CreatePrivateLiveRouteView() {
    const { from, exercise, card } = createPrivateLiveRoute.useSearch();
    return (
      <CreatePrivateLivePage
        fromCode={from}
        exercise={exercise ? { id: exercise, cardId: card ?? null } : undefined}
      />
    );
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  decouverteRoute,
  scratchTutorialRoute,
  onboardingRoute,
  parentPayRoute,
  packBuyRoute,
  expressBuyRoute,
  acheterRoute,
  bienvenueRoute,
  offertRoute,
  publicCardRoute,
  publicAnimationRoute,
  captureAnimationRoute,
  skillcardBuyRoute,
  payForStudentRoute,
  redeemGiftRoute,
  trousseGiftRoute,
  connectRoute,
  cardShortCodeRoute,
  rejoindreRoute,
  parentalConsentRoute,
  tutorPublicProfileRoute,
  reserverRoute,
  demoRoute,
  demoExerciseRoute,
  liveRoute,
  privateLiveRoute,
  appRoute.addChildren([
    tutorCardPrintRoute,
    packRevisionSheetRoute,
    learnRoute,
    learnExerciseRoute,
    learnEpreuveRoute,
    dagPlayerRoute,
    blogArticleRoute,
    themesIndexRoute,
    themeDetailRoute,
    tutorRoute.addChildren([
      tutorDashboardRoute,
      tutorSubscriptionRoute,
      tutorClassesRoute,
      tutorClassNewRoute,
      tutorClassDetailRoute,
      tutorClassProgressionRoute,
      tutorStudentsRoute,
      tutorStudentDetailRoute,
      tutorContactsRoute,
      tutorFilesRoute,
      tutorDiscussionsRoute,
      tutorBookingDashboardRoute,
      tutorBookingSlotsRoute,
      tutorVisioSeriesRoute,
      tutorBookingPaymentsRoute,
      tutorBookingNewRoute,
      tutorNewMeetingRoute,
      tutorExercisesRoute,
      tutorEpreuvesRoute,
      tutorExercisesNewRoute,
      tutorExercisesNewV2Route,
      tutorExerciseDetailRoute,
      tutorExerciseEditRoute,
      tutorExerciseEditV2Route,
      tutorExerciseTestRoute,
      tutorSettingsRoute,
      tutorNewsRoute,
      tutorThemesRoute,
      tutorThemeNewRoute,
      tutorThemeEditRoute,
      tutorBlogRoute,
      tutorBlogNewRoute,
      tutorBlogStatsRoute,
      tutorBlogDetailRoute,
      tutorBlueprintsRoute,
      tutorBlueprintsNewRoute,
      tutorBlueprintDetailRoute,
      tutorCardsRoute,
      tutorCardsNewRoute,
      tutorCardIdeasRoute,
      tutorDiagnosticRoute,
      tutorCardDetailRoute,
      tutorSubmissionsRoute,
      tutorAdminRoute.addChildren([
        tutorAdminChaptersRoute,
        tutorAdminChaptersNewRoute,
        tutorAdminChapterDetailRoute,
        tutorAdminChapterEditRoute,
        tutorAdminLessonDetailRoute,
        tutorAdminLessonEditRoute,
        tutorAdminLessonEditV2Route,
        tutorAdminCompetenceDetailRoute,
        tutorAdminExercisesRoute,
        tutorAdminExercisesNewRoute,
        tutorAdminExerciseDetailRoute,
        tutorAdminExerciseEditRoute,
        tutorAdminPromptsRoute,
        tutorAdminObjectivesRoute,
        tutorAdminFaqRoute,
        tutorAdminSkillcardPricingRoute,
        tutorAdminUsersRoute,
        tutorAdminObjectivesNewRoute,
        tutorAdminObjectiveDetailRoute,
        tutorAdminPacksRoute,
        tutorAdminToolboxRoute,
        tutorAdminTroussesRoute,
        tutorAdminAnimationsRoute,
        tutorAdminAnimationLabRoute,
        tutorAdminAnimationAudioRoute,
        tutorAdminAnimationTestRoute,
        tutorAdminAnimationStudioRoute,
        tutorAdminEpreuvesRoute,
        tutorAdminEpreuvesNewRoute,
        tutorAdminEpreuveDetailRoute,
        tutorAdminBlogModerationRoute,
        tutorAdminReportsRoute,
        tutorAdminStatsRoute,
        tutorAdminScratchFeedbackRoute,
        tutorAdminAndroidTestersRoute,
        tutorAdminSocialConnectedRoute,
        tutorAdminSocialSettingsRoute,
        tutorAdminConnectionsRoute,
        tutorAdminSchoolsRoute,
        tutorAdminNewsletterRoute,
        tutorAdminDagRoute,
        tutorAdminDemoRoute,
        tutorAdminThemesModerationRoute,
        tutorAdminShowcasePlaylistRoute,
        tutorAdminHeroRoute,
        tutorAdminPipelineRoute,
        tutorAdminPipelineNewRoute,
        tutorAdminPipelineRunRoute,
        tutorAdminPipelineReviewRoute,
        tutorAdminPipelineScenariosRoute,
        tutorAdminPipelineMatchingsRoute,
        tutorAdminYoutubeRoute,
        tutorAdminYoutubeNewRoute,
        tutorAdminYoutubeConnectRoute,
        tutorAdminYoutubeVideoRoute,
        tutorAdminChallengesRoute,
        tutorAdminChallengeRoute,
        tutorAdminDiffusionsRoute,
        tutorAdminLiveRoute,
        tutorAdminAlwaysdataRoute,
      ]),
    ]),
    parentRoute.addChildren([
      parentDashboardRoute,
      parentNewsRoute,
      parentChildrenRoute,
      parentDiagnosticRoute,
      parentSkillcardsRoute,
      parentSubscriptionsRoute,
      parentContactsRoute,
    ]),
    playerRoute.addChildren([
      playerDashboardRoute,
      playerNewsRoute,
      playerContactsRoute,
      playerJouerRoute,
      playerSkillcardsRoute,
    ]),
    profileRoute,
    accountPrivacyRoute,
    linkParentRoute,
    rendezVousJoinRoute,
    discussionThreadRoute,
    attemptReviewRoute,
    speakerVoicesRoute,
    livePrizeChoiceRoute,
    giftsRoute,
    trousseCollectionRoute,
    selfAssessmentRoute,
    createPrivateLiveRoute,
    rendezVousRoomRoute,
    freeMeetingRoomRoute,
    studentRoute.addChildren([
      studentDashboardRoute,
      studentObjectivesRoute,
      studentObjectiveDetailRoute,
      studentNewsRoute,
      studentReviseRoute,
      studentWarmupRoute,
      studentDiagnosticRoute,
      studentDiagnosticReportRoute,
      studentContactsRoute,
      studentFilesRoute,
      studentSkillcardsRoute,
      studentEvaluationsRoute,
      studentAutomatismesRoute,
      studentProgressionRoute,
      studentPackTestRoute,
      studentDiscussionsRoute,
      studentReserverCoursRoute,
      studentAcceptPeerRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  context: {
    auth: {
      session: null,
      user: null,
      profile: null,
      loading: true,
      profileLoading: false,
      profileError: null,
      refreshProfile: async () => {},
    },
    queryClient: undefined!,
  },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
